import React from 'react'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { EMkAuthenticationAction } from '@auth/types'
import { CHECK_EMAIL_STEP_ADDITIONAL_INFO_DEFAULT, CHECK_EMAIL_STEP_BUTTON_TEXT, REGISTER_LABELS } from '@constants'
import { useAuthLabelsContext } from '@features/account/contexts/AuthLabelsContext'
import { REGISTER_STEP_TO_BACKEND } from '@features/account/services/authLabels'
import { useUserDetails } from '@hooks/useUserDetails'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { useOkta } from '@hooks/useOkta'
import { getStepByName, getStepTitleByName } from './stepRegistry'
import { StepName } from './types'
import { FormHeader } from '../FormHeader'
import { useRegister } from '../../hooks/useRegister'

/** Closing the modal on these steps must clear provider state + local MFA handoff, or the next "Sign in" / "Create account" session looks like a half-finished MFA flow. */
const REGISTRATION_MFA_MODAL_STEPS: StepName[] = [StepName.MFA_SETUP_SELECTOR, StepName.MFA_SETUP_DETAILS, StepName.MFA_VERIFY_CODE]

const LOCK_SCREEN_STEPS: StepName[] = [StepName.ACCOUNT_LOCK_SCREEN, StepName.MFA_CRITICAL_LOCK_SCREEN]

/** MFA handoff keys use DataStore default (sessionStorage); DevUIPanel may still clear legacy localStorage copies—remove both. */
const MFA_HANDOFF_STORAGE_KEYS = ['stateToken', 'mfaFactorId', 'mfaNextLink'] as const

const removeMfaHandoffKeysFromStores = (): void => {
  try {
    MFA_HANDOFF_STORAGE_KEYS.forEach((key) => {
      DataStore.removeItem(key)
      DataStore.removeItem(key, { store: localStorage })
    })
  } catch {
    // ignore
  }
}

export interface RegisterProps {
  startStep?: StepName
}

export const Register: React.FC<RegisterProps> = ({ startStep }) => {
  const { state, setCurrentStep, resetState, loadRegistrationContext, clearRegistrationContext } = useRegister()

  const { firstName: sessionFirstName, lastName: sessionLastName } = useUserDetails()
  const { isMfaLocked, mfaLockoutEndTime, dispatchAuthenticationAction } = useAuthentication()
  const { oktaAuth } = useOkta()
  const { getLabel } = useAuthLabelsContext()

  const { currentStepName } = state
  const registerBackendStep = REGISTER_STEP_TO_BACKEND[currentStepName] ?? currentStepName

  const [contextCleared] = React.useState(() => {
    const isStartingAtAccountLookup = !startStep || startStep === StepName.ACCOUNT_LOOKUP
    if (isStartingAtAccountLookup) {
      const urlParams = new URLSearchParams(window.location.search)
      const hasActivationToken = urlParams.get('token') || urlParams.get('at')
      const existingContext = loadRegistrationContext()
      // Only consider it an activation flow if there's a token in the URL, not just in context
      // Old context tokens from previous registrations should not prevent clearing
      const isActivationFlow = hasActivationToken || (existingContext?.isActivationFlow && hasActivationToken)
      const hasSetInitialStep = sessionStorage.getItem('registerInitialStepSet') === 'true'

      if (!isActivationFlow && !hasSetInitialStep) {
        clearRegistrationContext()
        try {
          localStorage.removeItem('makoRegistrationContext')
        } catch (error) {
          // Ignore errors
        }
        return true
      }
    }
    return false
  })

  const registrationContext = React.useMemo(() => {
    try {
      if (contextCleared) {
        return null
      }
      return loadRegistrationContext()
    } catch (error) {
      return null
    }
  }, [loadRegistrationContext, contextCleared])
  const contextFirstName = registrationContext?.firstName || (registrationContext as any)?.user?.profile?.firstName || (registrationContext as any)?.user?.firstName || ''
  const contextLastName = registrationContext?.lastName || (registrationContext as any)?.user?.profile?.lastName || (registrationContext as any)?.user?.lastName || ''

  const [hasSetInitialStep, setHasSetInitialStep] = React.useState(() => sessionStorage.getItem('registerInitialStepSet') === 'true')

  const lastProcessedStartStepRef = React.useRef<StepName | undefined>(undefined)

  // Keep in sync with the lock-screen effect below: same predicate for "active" lockout window
  const isInActiveMfaLockout = Boolean(isMfaLocked && mfaLockoutEndTime && Date.now() < mfaLockoutEndTime)

  React.useEffect(() => {
    if (!startStep) return

    // While lockout is active, `startStep` may still be MFA_VERIFY from the modal, but the lock-screen
    // effect below must win (reset + critical lock). Re-applying startStep here calls setCurrentStep(verify)
    // and fights that, causing a setState loop and "Maximum update depth exceeded" (useRegistration dispatch).
    if (isInActiveMfaLockout) {
      return
    }

    const isMfaVerifyCodeStep = startStep === StepName.MFA_VERIFY_CODE
    const isLockScreenStep = startStep === StepName.ACCOUNT_LOCK_SCREEN || startStep === StepName.MFA_CRITICAL_LOCK_SCREEN
    // Sign-in opens register at MFA_ENROLL (selector) or MFA_ENROLL/verify: parent passes startStep while the modal
    // can stay mounted with hasSetInitialStep still true—still honor startStep after a reset to account lookup.
    const isSigninMfaHandoffToSelectorOrDetails =
      (startStep === StepName.MFA_SETUP_SELECTOR || startStep === StepName.MFA_SETUP_DETAILS) && currentStepName === StepName.ACCOUNT_LOOKUP

    if (isMfaVerifyCodeStep) {
      // (startStep !== current || startStep !== last) could stay true with current===start, causing repeated setCurrentStep → "Maximum update depth"
      if (currentStepName === startStep && lastProcessedStartStepRef.current === startStep) {
        if (!hasSetInitialStep) {
          setHasSetInitialStep(true)
          sessionStorage.setItem('registerInitialStepSet', 'true')
        }
        return
      }
      if (currentStepName !== startStep) {
        setCurrentStep(startStep)
      }
      setHasSetInitialStep(true)
      sessionStorage.setItem('registerInitialStepSet', 'true')
      lastProcessedStartStepRef.current = startStep
      return
    }

    if (startStep !== currentStepName) {
      const shouldApplyFromParent = !hasSetInitialStep || isSigninMfaHandoffToSelectorOrDetails
      if (!shouldApplyFromParent) {
        return
      }
      if (isLockScreenStep) {
        resetState()
      }
      setCurrentStep(startStep)
      setHasSetInitialStep(true)
      sessionStorage.setItem('registerInitialStepSet', 'true')
      lastProcessedStartStepRef.current = startStep
    }
  }, [startStep, currentStepName, hasSetInitialStep, setCurrentStep, resetState, isInActiveMfaLockout])

  React.useEffect(() => {
    if (isMfaLocked && mfaLockoutEndTime && Date.now() < mfaLockoutEndTime) {
      if (currentStepName !== StepName.MFA_CRITICAL_LOCK_SCREEN) {
        resetState()
        setCurrentStep(StepName.MFA_CRITICAL_LOCK_SCREEN)
      }
    }
  }, [isMfaLocked, mfaLockoutEndTime, currentStepName, setCurrentStep, resetState])

  const clearMfaOrVerifyModalDismissal = React.useCallback(() => {
    lastProcessedStartStepRef.current = undefined
    dispatchAuthenticationAction({ type: EMkAuthenticationAction.CLEAR_ERROR })
    clearRegistrationContext()
    resetState()
    try {
      sessionStorage.removeItem('registerInitialStepSet')
      sessionStorage.removeItem('mfaSetupData')
    } catch {
      // ignore
    }
    removeMfaHandoffKeysFromStores()
  }, [clearRegistrationContext, dispatchAuthenticationAction, resetState])

  const performLockScreenReset = React.useCallback(async () => {
    dispatchAuthenticationAction({ type: EMkAuthenticationAction.UNLOCK_MFA_ACCOUNT })
    dispatchAuthenticationAction({ type: EMkAuthenticationAction.UNLOCK_ACCOUNT })
    dispatchAuthenticationAction({ type: EMkAuthenticationAction.RESET_FAILED_MFA_ATTEMPTS })
    try {
      ProtectedDataStore.removeItem(EMkStorageKeys.MAKO_SESSION)
    } catch {
      // ignore
    }
    try {
      await oktaAuth?.tokenManager?.clear?.()
    } catch {
      // ignore
    }
    removeMfaHandoffKeysFromStores()
    lastProcessedStartStepRef.current = undefined
    try {
      sessionStorage.removeItem('registerInitialStepSet')
      sessionStorage.removeItem('mfaSetupData')
    } catch {
      // ignore
    }
    clearRegistrationContext()
    resetState()
  }, [dispatchAuthenticationAction, oktaAuth, clearRegistrationContext, resetState])

  const handleModalClose = React.useCallback(() => {
    if (LOCK_SCREEN_STEPS.includes(currentStepName)) {
      performLockScreenReset().catch(() => {
        // best-effort reset; errors already swallowed inside performLockScreenReset where possible
      })
      setHasSetInitialStep(false)
      return
    }
    if (REGISTRATION_MFA_MODAL_STEPS.includes(currentStepName)) {
      clearMfaOrVerifyModalDismissal()
      setHasSetInitialStep(false)
    }
  }, [currentStepName, performLockScreenReset, clearMfaOrVerifyModalDismissal, setHasSetInitialStep])

  React.useEffect(() => {
    window.addEventListener('modalClose', handleModalClose)
    return () => {
      window.removeEventListener('modalClose', handleModalClose)
    }
  }, [handleModalClose])

  const currentStepRef = React.useRef(currentStepName)
  const resetStateRef = React.useRef(resetState)
  const clearDismissalRef = React.useRef(clearMfaOrVerifyModalDismissal)

  React.useEffect(() => {
    currentStepRef.current = currentStepName
    resetStateRef.current = resetState
    clearDismissalRef.current = clearMfaOrVerifyModalDismissal
  }, [currentStepName, resetState, clearMfaOrVerifyModalDismissal])

  React.useEffect(
    () => () => {
      const currentStep = currentStepRef.current
      const checkEmailStep = 'checkEmail' as const

      const stepOrder = [
        'accountLookup',
        'userDetails',
        'checkEmail',
        'setPassword',
        'mfaSetupSelector',
        'mfaSetupDetails',
        'mfaVerifyCode',
        'accountLockScreen',
        'activationError',
      ]
      const currentStepIndex = stepOrder.indexOf(currentStep)
      const checkEmailStepIndex = stepOrder.indexOf(checkEmailStep)

      const isActivationFlow = Boolean((state as any)?.formData?.isActivationFlow?.value)
      const hasActivationToken = Boolean((state as any)?.formData?.activationToken?.value)
      const hasStateToken = Boolean((state as any)?.formData?.stateToken?.value)
      const startedAtMfaStep = (startStep && startStep.includes('mfa')) || sessionStorage.getItem('mfaSetupData') !== null
      const isOnInformationalStep = currentStep === checkEmailStep

      const shouldReset = (currentStepIndex < checkEmailStepIndex && !(isActivationFlow || hasActivationToken || hasStateToken || startedAtMfaStep)) || isOnInformationalStep

      if (REGISTRATION_MFA_MODAL_STEPS.includes(currentStep as StepName)) {
        lastProcessedStartStepRef.current = undefined
        clearDismissalRef.current()
        return
      }

      try {
        sessionStorage.removeItem('registerInitialStepSet')
      } catch {
        // ignore
      }
      lastProcessedStartStepRef.current = undefined

      if (shouldReset) {
        resetStateRef.current()
      }
    },
    [startStep],
  )

  const step = React.useMemo(() => {
    const resolvedStep = getStepByName(currentStepName)
    return resolvedStep
  }, [currentStepName])

  const headerTitle = React.useMemo(() => {
    const rawTitle = step?.content?.title
    let staticTitle: string
    if (rawTitle === undefined || rawTitle === null) {
      staticTitle = getStepTitleByName(currentStepName)
    } else if (Array.isArray(rawTitle)) {
      staticTitle = rawTitle.join(' ')
    } else {
      staticTitle = rawTitle
    }
    const base = getLabel('register', registerBackendStep, 'title', staticTitle)
    const useName = Boolean((step as any)?.props?.useRegistrantNameAsTitle)
    if (!useName) return base

    const formFirst = String((state as any)?.formData?.firstName?.value ?? '').trim()
    const formLast = String((state as any)?.formData?.lastName?.value ?? '').trim()
    const sessionFirst = String(sessionFirstName ?? '').trim()
    const sessionLast = String(sessionLastName ?? '').trim()
    const contextFirst = String(contextFirstName ?? '').trim()
    const contextLast = String(contextLastName ?? '').trim()
    const hasNamePlaceholder = /\{firstName\}|\{lastName\}/i.test(base)
    const baseWithReplacedTokens = base
      .replace(/\{firstName\}/gi, formFirst || sessionFirst || contextFirst)
      .replace(/\{lastName\}/gi, formLast || sessionLast || contextLast)
      .replace(/\s+/g, ' ')
      .trim()

    // Set-password header: first name only (CHP2-15742 / EBAC Figma).
    if (currentStepName === StepName.SET_PASSWORD) {
      const firstOnly = formFirst || sessionFirst || contextFirst
      if (firstOnly) {
        return firstOnly
      }
      if (hasNamePlaceholder) {
        return baseWithReplacedTokens || staticTitle
      }
      return base
    }

    if (currentStepName === StepName.MFA_SETUP_SELECTOR) {
      const firstOnly = formFirst || sessionFirst || contextFirst
      if (firstOnly) {
        return firstOnly
      }
      if (hasNamePlaceholder) {
        return staticTitle
      }
    }

    const formFullName = `${formFirst} ${formLast}`.trim()

    if (formFullName) {
      return formFullName
    }

    const sessionFullName = `${sessionFirst} ${sessionLast}`.trim()

    if (sessionFullName) {
      return sessionFullName
    }

    const contextFullName = `${contextFirst} ${contextLast}`.trim()

    if (contextFullName) {
      return contextFullName
    }

    if (baseWithReplacedTokens) {
      return baseWithReplacedTokens
    }

    return hasNamePlaceholder ? '' : base
  }, [state, step, currentStepName, sessionFirstName, sessionLastName, contextFirstName, contextLastName, step?.content?.title, getLabel, registerBackendStep])

  const LazyStep = React.useMemo(() => React.lazy(step?.loader ?? (() => Promise.resolve({ default: () => null }))), [step, currentStepName])

  const lazyStepProps = React.useMemo(() => {
    const base = { ...(step?.props || {}) } as Record<string, unknown>
    if (currentStepName === StepName.CHECK_EMAIL) {
      const defaultDescription = typeof base.description === 'string' ? base.description : ''
      const cmsDescription = getLabel('register', registerBackendStep, 'description', '')
      const cmsNote = getLabel('register', registerBackendStep, 'note', '')
      const cmsAdditionalInfo = getLabel('register', registerBackendStep, 'additionalInfo', '')
      const cmsBackToSignIn = getLabel('register', registerBackendStep, 'backToSignIn', '')

      base.description = cmsDescription || defaultDescription
      // completion step content historically used "additionalInfo"; current content-service uses "note".
      base.additionalInfo = cmsNote || cmsAdditionalInfo || CHECK_EMAIL_STEP_ADDITIONAL_INFO_DEFAULT
      base.buttonText = cmsBackToSignIn || CHECK_EMAIL_STEP_BUTTON_TEXT
    }
    return base
  }, [step, currentStepName, getLabel, registerBackendStep])

  if (!step) {
    return (
      <div className="register__step-not-found">
        <p>Step not found</p>
      </div>
    )
  }

  const isMfaVerifyStep = currentStepName === StepName.MFA_VERIFY_CODE

  return (
    <div className="register">
      <div className="register__header">
        <FormHeader
          className={isMfaVerifyStep ? 'mb-4' : 'mb-6'}
          icon={(step as any)?.props?.headerIcon ?? (step as any)?.content?.icon}
          iconSet={(step as any)?.props?.headerIconSet}
          iconClassName={(step as any)?.props?.headerIconClassName}
          iconAriaLabel={(step as any)?.props?.headerIconAriaLabel}
          caption={
            currentStepName === StepName.CHECK_EMAIL
              ? ''
              : getLabel(
                  'register',
                  registerBackendStep,
                  'caption',
                  (() => {
                    const c = step.content?.caption
                    if (c === undefined || c === null) return ''
                    return Array.isArray(c) ? c.join(' ') : c
                  })(),
                )
          }
          captionClassName="caption-1"
          title={headerTitle}
          titleTag="h1"
          titleClassName={(step as any)?.props?.headerTitleClassName ?? 'title-1'}
          imageSrc={(step as any).content?.imageSrc}
          imageAlt={(step as any).content?.imageAlt}
          imageWidth={(step as any).content?.imageWidth}
          imageHeight={(step as any).content?.imageHeight}
        />
      </div>
      <React.Suspense fallback={<div className="register__loading">{getLabel('register', 'common', 'loading', REGISTER_LABELS.LOADING)}…</div>}>
        <LazyStep
          className="register__step-container__step"
          {...lazyStepProps}
        />
      </React.Suspense>
    </div>
  )
}
